import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from '@ais-ui/custom-hooks';
import { UnifiedAtonItem } from './useUnifiedAtons';
import { AtonFilters, AtonSortType, SortConfig, SortDirection } from './types';

export const useAtonFilters = (
  atonItems: UnifiedAtonItem[],
  search: string
) => {
  const debouncedSearch = useDebounce(
    search.length >= 2 ? search : '',
    200,
    ''
  );

  const [atonFilters, setAtonFilters] = useState<AtonFilters>({
    type: 'all',
    state: 'all',
    owner: 'all',
  });

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    type: 'name',
    direction: 'asc',
  });

  const filterByType = useCallback(
    (
      items: UnifiedAtonItem[],
      typeFilter: AtonFilters['type']
    ): UnifiedAtonItem[] => {
      if (typeFilter === 'all') {
        return items;
      }
      return items.filter((item) => item.type === typeFilter);
    },
    []
  );

  const filterByState = useCallback(
    (
      items: UnifiedAtonItem[],
      stateFilter: AtonFilters['state']
    ): UnifiedAtonItem[] => {
      if (stateFilter === 'all') {
        return items;
      }
      const targetState = parseInt(stateFilter);
      return items.filter((item) => item.state === targetState);
    },
    []
  );

  const filterByOwner = useCallback(
    (
      items: UnifiedAtonItem[],
      ownerFilter: AtonFilters['owner']
    ): UnifiedAtonItem[] => {
      if (ownerFilter === 'all') {
        return items;
      }
      return items.filter((item) => item.owner === ownerFilter);
    },
    []
  );

  const filterBySearch = useCallback(
    (items: UnifiedAtonItem[], searchTerm: string): UnifiedAtonItem[] => {
      if (!searchTerm) return items;

      const lowerSearch = searchTerm.toLowerCase();
      return items.filter(
        (item) =>
          item.name.toLowerCase().includes(lowerSearch) ||
          item.owner?.toLowerCase().includes(lowerSearch) ||
          item.siteType?.toLowerCase().includes(lowerSearch) ||
          item.ipAddress?.toLowerCase().includes(lowerSearch)
      );
    },
    []
  );

  const sortAtons = useCallback(
    (items: UnifiedAtonItem[], config: SortConfig): UnifiedAtonItem[] => {
      const sorted = [...items];
      const { type: sortBy, direction } = config;

      switch (sortBy) {
        case 'name':
          sorted.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'type':
          sorted.sort((a, b) => a.type.localeCompare(b.type));
          break;
        case 'state':
          sorted.sort((a, b) => {
            const stateA = a.state ?? 0;
            const stateB = b.state ?? 0;
            return stateA - stateB;
          });
          break;
        case 'owner':
          sorted.sort((a, b) => (a.owner || '').localeCompare(b.owner || ''));
          break;
      }

      if (direction === 'desc') {
        sorted.reverse();
      }

      return sorted;
    },
    []
  );

  // Get unique owners for filter dropdown
  const availableOwners = useMemo(() => {
    const owners = new Set<string>();
    atonItems.forEach((item) => {
      if (item.owner) {
        owners.add(item.owner);
      }
    });
    return Array.from(owners).sort();
  }, [atonItems]);

  const filteredAndSortedAtons = useMemo(() => {
    if (!atonItems) return [];

    let filtered = atonItems;

    filtered = filterBySearch(filtered, debouncedSearch);
    filtered = filterByType(filtered, atonFilters.type);
    filtered = filterByState(filtered, atonFilters.state);
    filtered = filterByOwner(filtered, atonFilters.owner);

    return sortAtons(filtered, sortConfig);
  }, [
    debouncedSearch,
    filterBySearch,
    filterByType,
    filterByState,
    filterByOwner,
    atonFilters.type,
    atonFilters.state,
    atonFilters.owner,
    atonItems,
    sortAtons,
    sortConfig,
  ]);

  return {
    filteredAndSortedAtons,
    atonFilters,
    setAtonFilters,
    sortConfig,
    setSortConfig,
    availableOwners,
  };
};
